import React, { Component } from "react";
import {
    Paper,
    Box,
    Typography,
    TextField,
    Button,
    Checkbox,
    Chip,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    InputAdornment,
    IconButton,
    Divider,
    Alert
} from '@mui/material';
import {
    Send,
    Preview,
    Email,
    Search,
    Close,
    People,
    Warning,
    Error,
    Info,
    CheckCircleOutline,
    Star,
    ShoppingCart
} from '@mui/icons-material';

import AdminAPI from "../service/AdminAPI";
import ClientAPI from "../service/ClientAPI";
import Events from '../service/Events';
import LocalStorageManager from "../managers/LocalStorageManager";

class DashboardEmailMarketing extends Component {
    constructor(props) {
        super(props);

        this.state = {
            allCustomers: [],
            products: [],
            selectedCustomers: [],
            searchFilter: '',
            
            // Custom emails
            customEmails: [],
            customEmailInput: '',
            
            // Email content
            emailSubject: '',
            emailBody: '',
            
            // Preview dialog
            showPreview: false,
            previewHTML: '',
            
            // Send status
            sending: false,
            
            // Validation
            tagValidationErrors: [],
            
            loaded: false
        };

        this.isAdmin = LocalStorageManager.Get("admin") === "1";
        this.emailBodyRef = React.createRef();
        this.validateDebounceTimer = null;
    }

    componentDidMount() {
        if (!this.isAdmin) {
            window.location.href = "/";
            return;
        }

        // Fetch customers and products
        AdminAPI.FetchUserOrders()
            .then(allOrders => {
                ClientAPI.RequestProducts()
                    .then(products => {
                        this.setState({ 
                            allCustomers: allOrders.filter(c => !c.isPending && c.email),
                            products: products,
                            loaded: true 
                        });
                    })
                    .catch(console.error);
            })
            .catch(console.error);
    }

    handleCustomerSelect = (customer) => {
        const { selectedCustomers } = this.state;
        const isSelected = selectedCustomers.some(c => c.userID === customer.userID);
        
        if (isSelected) {
            this.setState({
                selectedCustomers: selectedCustomers.filter(c => c.userID !== customer.userID)
            });
        } else {
            this.setState({
                selectedCustomers: [...selectedCustomers, customer]
            });
        }
    };

    handleSelectAll = () => {
        const filteredCustomers = this.getFilteredCustomers();
        if (this.state.selectedCustomers.length === filteredCustomers.length) {
            this.setState({ selectedCustomers: [] });
        } else {
            this.setState({ selectedCustomers: [...filteredCustomers] });
        }
    };

    getFilteredCustomers = () => {
        const { allCustomers, searchFilter } = this.state;
        
        if (!searchFilter) {
            return allCustomers;
        }

        const searchPattern = searchFilter.toLowerCase();
        return allCustomers.filter(customer => {
            const fullName = `${customer.firstName} ${customer.lastName}`.toLowerCase();
            const email = customer.email ? customer.email.toLowerCase() : '';
            
            return fullName.includes(searchPattern) || email.includes(searchPattern);
        });
    };

    quickFilterAll = () => {
        this.setState({ selectedCustomers: [...this.state.allCustomers] });
    };

    quickFilterNone = () => {
        this.setState({ selectedCustomers: [] });
    };

    quickFilterWithOrders = () => {
        const customersWithOrders = this.state.allCustomers.filter(c => c.orders && c.orders.length > 0);
        this.setState({ selectedCustomers: customersWithOrders });
    };

    quickFilterWithoutOrders = () => {
        const customersWithoutOrders = this.state.allCustomers.filter(c => !c.orders || c.orders.length === 0);
        this.setState({ selectedCustomers: customersWithoutOrders });
    };

    handleAddCustomEmail = () => {
        const email = this.state.customEmailInput.trim();
        
        // Basic email validation
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        
        if (!email) {
            return;
        }
        
        if (!emailRegex.test(email)) {
            Events.onResult.emit('error', 'Bitte geben Sie eine gültige E-Mail-Adresse ein.');
            return;
        }
        
        // Check if email already exists in custom emails
        if (this.state.customEmails.includes(email)) {
            Events.onResult.emit('warning', 'Diese E-Mail-Adresse wurde bereits hinzugefügt.');
            return;
        }
        
        // Check if email already exists in selected customers
        if (this.state.selectedCustomers.some(c => c.email === email)) {
            Events.onResult.emit('warning', 'Diese E-Mail-Adresse ist bereits als Kunde ausgewählt.');
            return;
        }
        
        this.setState({
            customEmails: [...this.state.customEmails, email],
            customEmailInput: ''
        });
    };

    handleRemoveCustomEmail = (emailToRemove) => {
        this.setState({
            customEmails: this.state.customEmails.filter(email => email !== emailToRemove)
        });
    };

    handleCustomEmailInputKeyPress = (e) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            this.handleAddCustomEmail();
        }
    };

    // Insert placeholder at cursor position
    insertPlaceholder = (placeholder) => {
        const textarea = this.emailBodyRef.current && this.emailBodyRef.current.querySelector('textarea');
        if (!textarea) return;

        const start = textarea.selectionStart;
        const end = textarea.selectionEnd;
        const text = this.state.emailBody;
        
        const before = text.substring(0, start);
        const after = text.substring(end);
        const newText = before + placeholder + after;
        
        this.setState({ emailBody: newText }, () => {
            // Restore cursor position after the inserted placeholder
            textarea.focus();
            const newCursorPos = start + placeholder.length;
            textarea.setSelectionRange(newCursorPos, newCursorPos);
            this.validateTags();
        });
    };

    // Insert styled section with START and END tags
    insertStyledSection = (type) => {
        const textarea = this.emailBodyRef.current && this.emailBodyRef.current.querySelector('textarea');
        if (!textarea) return;

        const start = textarea.selectionStart;
        const end = textarea.selectionEnd;
        const text = this.state.emailBody;
        const selectedText = text.substring(start, end);
        
        const before = text.substring(0, start);
        const after = text.substring(end);
        
        const startTag = `{{${type}_START}}`;
        const endTag = `{{${type}_END}}`;
        
        let newText;
        let newCursorPos;
        
        if (selectedText) {
            // Wrap selected text
            newText = before + startTag + '\n' + selectedText + '\n' + endTag + after;
            newCursorPos = start + startTag.length + 1 + selectedText.length + 1 + endTag.length;
        } else {
            // Insert template with placeholder text
            newText = before + startTag + '\n' + 'Ihr Text hier...' + '\n' + endTag + after;
            newCursorPos = start + startTag.length + 1;
        }
        
        this.setState({ emailBody: newText }, () => {
            textarea.focus();
            if (selectedText) {
                textarea.setSelectionRange(newCursorPos, newCursorPos);
            } else {
                // Select the placeholder text for easy replacement
                const placeholderStart = start + startTag.length + 1;
                const placeholderEnd = placeholderStart + 'Ihr Text hier...'.length;
                textarea.setSelectionRange(placeholderStart, placeholderEnd);
            }
            this.validateTags();
        });
    };

    // Validate that all START tags have matching END tags (debounced)
    debouncedValidate = () => {
        if (this.validateDebounceTimer) {
            clearTimeout(this.validateDebounceTimer);
        }
        this.validateDebounceTimer = setTimeout(() => {
            this.validateTags();
        }, 300);
    };

    // Validate that all START tags have matching END tags
    validateTags = () => {
        const { emailBody } = this.state;
        const errors = [];
        
        const tagTypes = ['WARNING', 'ERROR', 'INFO', 'SUCCESS', 'HIGHLIGHT'];
        
        tagTypes.forEach(type => {
            const startRegex = new RegExp(`\\{\\{\\s*${type}_START\\s*\\}\\}`, 'gi');
            const endRegex = new RegExp(`\\{\\{\\s*${type}_END\\s*\\}\\}`, 'gi');
            
            const startMatches = (emailBody.match(startRegex) || []).length;
            const endMatches = (emailBody.match(endRegex) || []).length;
            
            if (startMatches > endMatches) {
                errors.push(`${type}: ${startMatches - endMatches} fehlende(s) END Tag(s)`);
            } else if (endMatches > startMatches) {
                errors.push(`${type}: ${endMatches - startMatches} fehlende(s) START Tag(s)`);
            }
        });
        
        this.setState({ tagValidationErrors: errors });
    };

    generateStyledSection = (content, type) => {
        const styles = {
            warning: {
                background: '#fff3cd',
                border: '#ffc107',
                color: '#856404',
                icon: '⚠️'
            },
            error: {
                background: '#f8d7da',
                border: '#dc3545',
                color: '#721c24',
                icon: '❌'
            },
            info: {
                background: '#d1ecf1',
                border: '#17a2b8',
                color: '#0c5460',
                icon: 'ℹ️'
            },
            success: {
                background: '#d4edda',
                border: '#28a745',
                color: '#155724',
                icon: '✓'
            },
            highlight: {
                background: '#e7f3ff',
                border: '#2196f3',
                color: '#0d47a1',
                icon: '★'
            }
        };

        const style = styles[type] || styles.info;

        return `
            <div style="background-color: ${style.background}; border-left: 4px solid ${style.border}; padding: 15px; margin: 20px 10px; border-radius: 4px;">
                <p style="color: ${style.color}; font-size: 14px; margin: 0; line-height: 1.6;">
                    <strong>${style.icon} ${type.charAt(0).toUpperCase() + type.slice(1)}:</strong> ${content}
                </p>
            </div>
        `;
    };

    buildEmailHTML = () => {
        const { emailBody, products } = this.state;
        
        // Process styled sections first
        let processedContent = emailBody || '';

        // Define styled section patterns
        const sectionPatterns = [
            { type: 'warning', start: /\{\{\s*WARNING_START\s*\}\}/gi, end: /\{\{\s*WARNING_END\s*\}\}/gi },
            { type: 'error', start: /\{\{\s*ERROR_START\s*\}\}/gi, end: /\{\{\s*ERROR_END\s*\}\}/gi },
            { type: 'info', start: /\{\{\s*INFO_START\s*\}\}/gi, end: /\{\{\s*INFO_END\s*\}\}/gi },
            { type: 'success', start: /\{\{\s*SUCCESS_START\s*\}\}/gi, end: /\{\{\s*SUCCESS_END\s*\}\}/gi },
            { type: 'highlight', start: /\{\{\s*HIGHLIGHT_START\s*\}\}/gi, end: /\{\{\s*HIGHLIGHT_END\s*\}\}/gi }
        ];

        // Process each styled section type
        sectionPatterns.forEach(({ type, start, end }) => {
            const startMarker = `__${type.toUpperCase()}_START__`;
            const endMarker = `__${type.toUpperCase()}_END__`;
            
            processedContent = processedContent.replace(start, startMarker);
            processedContent = processedContent.replace(end, endMarker);
        });

        let html = '<div style="font-family: Arial, sans-serif; max-width: 100%; width: 100%; margin: 0 auto; padding: 10px; box-sizing: border-box;"><div style="max-width: 800px; margin: 0 auto; width: 100%;">';
        
        if (processedContent) {
            const paragraphs = processedContent.split('\n').filter(p => p.trim());
            paragraphs.forEach(paragraph => {
                // Check for styled section markers
                let isStyledMarker = false;
                sectionPatterns.forEach(({ type }) => {
                    const startMarker = `__${type.toUpperCase()}_START__`;
                    const endMarker = `__${type.toUpperCase()}_END__`;
                    
                    if (paragraph.includes(startMarker)) {
                        isStyledMarker = true;
                        html += `<div data-section-start="${type}">`;
                    } else if (paragraph.includes(endMarker)) {
                        isStyledMarker = true;
                        // Find the opening tag
                        const match = html.match(new RegExp(`<div data-section-start="${type}">(.+?)$`, 's'));
                        if (match) {
                            const sectionContent = match[1].replace(/<p[^>]*>|<\/p>/g, ' ').trim();
                            html = html.replace(match[0], this.generateStyledSection(sectionContent, type));
                        }
                    }
                });
                
                if (!isStyledMarker) {
                    // Check if this paragraph contains {{PRODUKTE}} placeholder
                    if (paragraph.includes('{{PRODUKTE}}') || paragraph.includes('{{ PRODUKTE }}')) {
                        const publicProducts = products.filter(p => p.Published === 1 || p.Published === true);
                        if (publicProducts.length > 0) {
                            const productHTML = `
                                <div style="margin: 20px 0; padding: 15px; background-color: #f8f9fa; border-radius: 8px; overflow-x: auto;">
                                    <h3 style="color: #2e7d32; margin-bottom: 15px; font-size: 18px;">Verfügbare Produkte:</h3>
                                    <table style="width: 100%; min-width: 100%; border-collapse: collapse; table-layout: auto;">
                                        <thead>
                                            <tr style="background-color: #2e7d32; color: white;">
                                                <th style="padding: 12px 10px; text-align: left; font-size: 14px; white-space: nowrap;">Produktname</th>
                                                <th style="padding: 12px 10px; text-align: right; font-size: 14px; white-space: nowrap;">Preis</th>
                                                <th style="padding: 12px 10px; text-align: right; font-size: 14px; white-space: nowrap;">Gewicht</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            ${publicProducts.map((p, idx) => `
                                                <tr style="border-bottom: 1px solid #ddd; background-color: ${idx % 2 === 0 ? 'white' : '#f9f9f9'};">
                                                    <td style="padding: 12px 10px; font-size: 14px;">${p.Name}</td>
                                                    <td style="padding: 12px 10px; text-align: right; font-size: 14px; white-space: nowrap;">${p.Price.toFixed(2)}€</td>
                                                    <td style="padding: 12px 10px; text-align: right; font-size: 14px; white-space: nowrap;">${p.Weight.toFixed(2)} Kg/L</td>
                                                </tr>
                                            `).join('')}
                                        </tbody>
                                    </table>
                                </div>
                            `;
                            html += paragraph.replace(/\{\{\s*PRODUKTE\s*\}\}/g, productHTML);
                        } else {
                            html += `<p style="font-size: 15px; line-height: 1.6; margin-bottom: 15px; padding: 0 10px;">${paragraph.replace(/\{\{\s*PRODUKTE\s*\}\}/g, '<em>(Keine veröffentlichten Produkte)</em>')}</p>`;
                        }
                    } else {
                        html += `<p style="font-size: 15px; line-height: 1.6; margin-bottom: 15px; padding: 0 10px;">${paragraph}</p>`;
                    }
                }
            });
        }
        
        html += '</div></div>';
        
        return html;
    };

    handlePreview = () => {
        if (this.state.tagValidationErrors.length > 0) {
            Events.onResult.emit('warning', 'Warnung: Es gibt fehlende Tags. Die Vorschau könnte nicht korrekt sein.');
        }
        
        const html = this.buildEmailHTML();
        this.setState({ 
            showPreview: true,
            previewHTML: html 
        });
    };

    handleSend = () => {
        const { selectedCustomers, customEmails, emailSubject, emailBody } = this.state;
        
        const totalRecipients = selectedCustomers.length + customEmails.length;
        
        if (totalRecipients === 0) {
            Events.onResult.emit('error', 'Bitte wählen Sie mindestens einen Empfänger aus oder fügen Sie eine E-Mail-Adresse hinzu.');
            return;
        }
        
        if (!emailSubject.trim()) {
            Events.onResult.emit('error', 'Bitte geben Sie einen Betreff ein.');
            return;
        }
        
        if (!emailBody.trim()) {
            Events.onResult.emit('error', 'Bitte geben Sie einen Nachrichtentext ein.');
            return;
        }
        
        if (this.state.tagValidationErrors.length > 0) {
            Events.onResult.emit('error', 'Bitte korrigieren Sie die fehlenden Tags, bevor Sie die E-Mail senden.');
            return;
        }
        
        const recipientText = totalRecipients === 1 ? '1 Empfänger' : `${totalRecipients} Empfänger`;
        const detailText = [];
        if (selectedCustomers.length > 0) {
            detailText.push(`${selectedCustomers.length} Kunde(n)`);
        }
        if (customEmails.length > 0) {
            detailText.push(`${customEmails.length} benutzerdefinierte E-Mail(s)`);
        }
        
        Events.onAlert.emit(
            "warning",
            `E-Mail an ${recipientText} senden?`,
            `Die E-Mail wird an ${detailText.join(' und ')} gesendet. Dieser Vorgang kann nicht rückgängig gemacht werden.`,
            () => {
                this.setState({ sending: true });
                
                // Combine customer emails and custom emails
                const customerEmails = selectedCustomers.map(c => c.email);
                const allRecipients = [...customerEmails, ...customEmails];
                
                AdminAPI.SendCustomEmail(allRecipients, emailSubject, emailBody)
                    .then((result) => {
                        if (result.totalFailed > 0) {
                            Events.onResult.emit('warning', 
                                `E-Mails gesendet: ${result.totalSent} erfolgreich, ${result.totalFailed} fehlgeschlagen.`
                            );
                        } else {
                            Events.onResult.emit('success', 
                                `E-Mail erfolgreich an ${result.totalSent} Empfänger gesendet!`
                            );
                            // Clear form
                            this.setState({
                                emailSubject: '',
                                emailBody: '',
                                selectedCustomers: [],
                                customEmails: []
                            });
                        }
                        this.setState({ sending: false });
                    })
                    .catch((error) => {
                        console.error(error);
                        Events.onResult.emit('error', 'Fehler beim Senden der E-Mails.');
                        this.setState({ sending: false });
                    });
            }
        );
    };

    render() {
        if (!this.state.loaded) {
            return (
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '50vh' }}>
                    <Typography>Lädt...</Typography>
                </Box>
            );
        }

        const filteredCustomers = this.getFilteredCustomers();
        const allSelected = this.state.selectedCustomers.length === filteredCustomers.length && filteredCustomers.length > 0;

        return (
            <div style={{ minHeight: "95vh", paddingBottom: "80px" }}>
                <h3 style={{
                    textAlign: "center",
                    marginTop: "35px",
                    marginBottom: "30px",
                    fontSize: "2.2rem",
                    fontWeight: 700,
                    color: "#2e7d32"
                }}>
                    E-Mail Dashboard
                </h3>

                <div style={{ maxWidth: "80%", margin: "auto" }}>
                    <Box sx={{ display: 'flex', flexDirection: { xs: 'column', lg: 'row' }, gap: 3 }}>
                        {/* Left side - Customer Selection */}
                        <Box sx={{ flex: 1 }}>
                            <Paper elevation={3} sx={{ p: 3, borderRadius: 3, mb: 3 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
                                    <People sx={{ fontSize: '2rem', color: '#2e7d32' }} />
                                    <Typography variant="h5" sx={{ fontWeight: 700, color: '#2e7d32' }}>
                                        Empfänger auswählen
                                    </Typography>
                                </Box>

                                {/* Quick Filters */}
                                <Box sx={{ mb: 3 }}>
                                    <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600, color: '#666' }}>
                                        Schnellfilter:
                                    </Typography>
                                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                                        <Chip
                                            label="Alle"
                                            onClick={this.quickFilterAll}
                                            color="primary"
                                            variant="outlined"
                                            sx={{ fontWeight: 600 }}
                                        />
                                        <Chip
                                            label="Keine"
                                            onClick={this.quickFilterNone}
                                            variant="outlined"
                                            sx={{ fontWeight: 600 }}
                                        />
                                        <Chip
                                            label="Mit Bestellungen"
                                            onClick={this.quickFilterWithOrders}
                                            color="success"
                                            variant="outlined"
                                            sx={{ fontWeight: 600 }}
                                        />
                                        <Chip
                                            label="Ohne Bestellungen"
                                            onClick={this.quickFilterWithoutOrders}
                                            variant="outlined"
                                            sx={{ fontWeight: 600 }}
                                        />
                                    </Box>
                                </Box>

                                {/* Search */}
                                <TextField
                                    fullWidth
                                    size="medium"
                                    placeholder="Kunden suchen..."
                                    value={this.state.searchFilter}
                                    onChange={(e) => this.setState({ searchFilter: e.target.value })}
                                    InputProps={{
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                <Search sx={{ color: "#2e7d32" }} />
                                            </InputAdornment>
                                        ),
                                    }}
                                    sx={{
                                        mb: 2,
                                        '& .MuiOutlinedInput-root': {
                                            borderRadius: '10px',
                                            '&:hover fieldset': {
                                                borderColor: '#2e7d32',
                                            },
                                            '&.Mui-focused fieldset': {
                                                borderColor: '#2e7d32',
                                            },
                                        }
                                    }}
                                />

                                {/* Selected count */}
                                <Box sx={{ mb: 2 }}>
                                    <Chip
                                        label={`${this.state.selectedCustomers.length} von ${filteredCustomers.length} ausgewählt`}
                                        color="success"
                                        sx={{ fontWeight: 600 }}
                                    />
                                </Box>

                                {/* Customer List */}
                                <TableContainer sx={{ maxHeight: 400, border: '1px solid #e0e0e0', borderRadius: 2 }}>
                                    <Table stickyHeader size="small">
                                        <TableHead>
                                            <TableRow>
                                                <TableCell padding="checkbox">
                                                    <Checkbox
                                                        checked={allSelected}
                                                        indeterminate={this.state.selectedCustomers.length > 0 && !allSelected}
                                                        onChange={this.handleSelectAll}
                                                        sx={{
                                                            color: '#2e7d32',
                                                            '&.Mui-checked': { color: '#2e7d32' }
                                                        }}
                                                    />
                                                </TableCell>
                                                <TableCell sx={{ fontWeight: 700 }}>Name</TableCell>
                                                <TableCell sx={{ fontWeight: 700 }}>E-Mail</TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {filteredCustomers.map((customer) => {
                                                const isSelected = this.state.selectedCustomers.some(c => c.userID === customer.userID);
                                                return (
                                                    <TableRow
                                                        key={customer.userID}
                                                        hover
                                                        onClick={() => this.handleCustomerSelect(customer)}
                                                        sx={{ cursor: 'pointer' }}
                                                    >
                                                        <TableCell padding="checkbox">
                                                            <Checkbox
                                                                checked={isSelected}
                                                                sx={{
                                                                    color: '#2e7d32',
                                                                    '&.Mui-checked': { color: '#2e7d32' }
                                                                }}
                                                            />
                                                        </TableCell>
                                                        <TableCell>{`${customer.firstName} ${customer.lastName}`}</TableCell>
                                                        <TableCell>{customer.email}</TableCell>
                                                    </TableRow>
                                                );
                                            })}
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                            </Paper>
                        </Box>

                        {/* Right side - Email Composer */}
                        <Box sx={{ flex: 1 }}>
                            {/* Custom Email Addresses */}
                            <Paper elevation={3} sx={{ p: 3, borderRadius: 3, mb: 3 }}>
                                <Typography variant="h6" sx={{ fontWeight: 700, color: '#2e7d32', mb: 2 }}>
                                    Benutzerdefinierte E-Mail-Adressen
                                </Typography>
                                <Typography variant="body2" sx={{ color: '#666', mb: 2 }}>
                                    Fügen Sie E-Mail-Adressen hinzu, die noch keine registrierten Kunden sind.
                                </Typography>
                                
                                {/* Custom Email Input */}
                                <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                                    <TextField
                                        fullWidth
                                        size="small"
                                        placeholder="email@beispiel.de"
                                        value={this.state.customEmailInput}
                                        onChange={(e) => this.setState({ customEmailInput: e.target.value })}
                                        onKeyPress={this.handleCustomEmailInputKeyPress}
                                        InputProps={{
                                            startAdornment: (
                                                <InputAdornment position="start">
                                                    <Email sx={{ color: "#2e7d32", fontSize: '1.2rem' }} />
                                                </InputAdornment>
                                            ),
                                        }}
                                        sx={{
                                            '& .MuiOutlinedInput-root': {
                                                borderRadius: '10px',
                                                '&:hover fieldset': {
                                                    borderColor: '#2e7d32',
                                                },
                                                '&.Mui-focused fieldset': {
                                                    borderColor: '#2e7d32',
                                                },
                                            }
                                        }}
                                    />
                                    <Button
                                        variant="contained"
                                        onClick={this.handleAddCustomEmail}
                                        sx={{
                                            bgcolor: '#2e7d32',
                                            '&:hover': { bgcolor: '#1b5e20' },
                                            minWidth: '100px'
                                        }}
                                    >
                                        Hinzufügen
                                    </Button>
                                </Box>

                                {/* Custom Emails List */}
                                {this.state.customEmails.length > 0 && (
                                    <Box sx={{ mt: 2 }}>
                                        <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600, color: '#666' }}>
                                            Hinzugefügte E-Mails ({this.state.customEmails.length}):
                                        </Typography>
                                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                                            {this.state.customEmails.map((email, idx) => (
                                                <Chip
                                                    key={idx}
                                                    label={email}
                                                    onDelete={() => this.handleRemoveCustomEmail(email)}
                                                    sx={{
                                                        bgcolor: '#e3f2fd',
                                                        color: '#1976d2',
                                                        fontWeight: 500,
                                                        '& .MuiChip-deleteIcon': {
                                                            color: '#1976d2',
                                                            '&:hover': {
                                                                color: '#d32f2f'
                                                            }
                                                        }
                                                    }}
                                                />
                                            ))}
                                        </Box>
                                    </Box>
                                )}

                                {/* Total Recipients Summary */}
                                {(this.state.selectedCustomers.length > 0 || this.state.customEmails.length > 0) && (
                                    <Box sx={{ mt: 3, p: 2, bgcolor: '#f8f9fa', borderRadius: 2 }}>
                                        <Typography variant="body2" sx={{ fontWeight: 600, color: '#333' }}>
                                            Gesamte Empfänger: {this.state.selectedCustomers.length + this.state.customEmails.length}
                                        </Typography>
                                        <Typography variant="caption" sx={{ color: '#666' }}>
                                            {this.state.selectedCustomers.length} Kunde(n) + {this.state.customEmails.length} benutzerdefinierte E-Mail(s)
                                        </Typography>
                                    </Box>
                                )}
                            </Paper>
                            <Paper elevation={3} sx={{ p: 3, borderRadius: 3 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
                                    <Email sx={{ fontSize: '2rem', color: '#2e7d32' }} />
                                    <Typography variant="h5" sx={{ fontWeight: 700, color: '#2e7d32' }}>
                                        E-Mail verfassen
                                    </Typography>
                                </Box>

                                {/* Template Info */}
                                <Alert severity="info" sx={{ mb: 3 }}>
                                    <Typography variant="body2">
                                        💡 <strong>Tipp:</strong> Verwenden Sie die Buttons unten, um Platzhalter einzufügen. 
                                        Gestylte Bereiche werden automatisch mit START- und END-Tags eingefügt. 
                                        Sie können auch Text markieren, bevor Sie einen Button klicken, um ihn zu umschließen.
                                    </Typography>
                                </Alert>

                                {/* Email Subject */}
                                <TextField
                                    fullWidth
                                    label="Betreff"
                                    value={this.state.emailSubject}
                                    onChange={(e) => this.setState({ emailSubject: e.target.value })}
                                    sx={{ mb: 2 }}
                                />

                                {/* Email Body */}
                                <Box sx={{ mb: 1 }}>
                                    <Typography variant="subtitle2" sx={{ mb: 1, color: '#666', fontWeight: 600 }}>
                                        Nachricht
                                    </Typography>
                                    
                                    {/* Placeholder Toolbar */}
                                    <Paper elevation={1} sx={{ p: 1.5, mb: 1, bgcolor: '#f8f9fa', borderRadius: 2 }}>
                                        <Typography variant="caption" sx={{ display: 'block', mb: 1, color: '#666', fontWeight: 600 }}>
                                            Platzhalter einfügen:
                                        </Typography>
                                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                                            <Button
                                                size="small"
                                                variant="outlined"
                                                startIcon={<ShoppingCart />}
                                                onClick={() => this.insertPlaceholder('{{PRODUKTE}}')}
                                                sx={{
                                                    borderColor: '#2e7d32',
                                                    color: '#2e7d32',
                                                    textTransform: 'none',
                                                    '&:hover': { borderColor: '#1b5e20', bgcolor: '#e8f5e9' }
                                                }}
                                            >
                                                Produktliste
                                            </Button>
                                            <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />
                                            <Button
                                                size="small"
                                                variant="outlined"
                                                startIcon={<Warning />}
                                                onClick={() => this.insertStyledSection('WARNING')}
                                                sx={{
                                                    borderColor: '#ffc107',
                                                    color: '#856404',
                                                    textTransform: 'none',
                                                    '&:hover': { borderColor: '#ff9800', bgcolor: '#fff3cd' }
                                                }}
                                            >
                                                Warnung
                                            </Button>
                                            <Button
                                                size="small"
                                                variant="outlined"
                                                startIcon={<Error />}
                                                onClick={() => this.insertStyledSection('ERROR')}
                                                sx={{
                                                    borderColor: '#dc3545',
                                                    color: '#721c24',
                                                    textTransform: 'none',
                                                    '&:hover': { borderColor: '#c82333', bgcolor: '#f8d7da' }
                                                }}
                                            >
                                                Fehler
                                            </Button>
                                            <Button
                                                size="small"
                                                variant="outlined"
                                                startIcon={<Info />}
                                                onClick={() => this.insertStyledSection('INFO')}
                                                sx={{
                                                    borderColor: '#17a2b8',
                                                    color: '#0c5460',
                                                    textTransform: 'none',
                                                    '&:hover': { borderColor: '#138496', bgcolor: '#d1ecf1' }
                                                }}
                                            >
                                                Info
                                            </Button>
                                            <Button
                                                size="small"
                                                variant="outlined"
                                                startIcon={<CheckCircleOutline />}
                                                onClick={() => this.insertStyledSection('SUCCESS')}
                                                sx={{
                                                    borderColor: '#28a745',
                                                    color: '#155724',
                                                    textTransform: 'none',
                                                    '&:hover': { borderColor: '#218838', bgcolor: '#d4edda' }
                                                }}
                                            >
                                                Erfolg
                                            </Button>
                                            <Button
                                                size="small"
                                                variant="outlined"
                                                startIcon={<Star />}
                                                onClick={() => this.insertStyledSection('HIGHLIGHT')}
                                                sx={{
                                                    borderColor: '#2196f3',
                                                    color: '#0d47a1',
                                                    textTransform: 'none',
                                                    '&:hover': { borderColor: '#1976d2', bgcolor: '#e7f3ff' }
                                                }}
                                            >
                                                Highlight
                                            </Button>
                                        </Box>
                                    </Paper>

                                    <TextField
                                        fullWidth
                                        multiline
                                        rows={10}
                                        value={this.state.emailBody}
                                        onChange={(e) => {
                                            this.setState({ emailBody: e.target.value });
                                            this.debouncedValidate();
                                        }}
                                        placeholder="Geben Sie hier Ihre Nachricht ein..."
                                        ref={this.emailBodyRef}
                                        sx={{
                                            '& .MuiOutlinedInput-root': {
                                                fontFamily: 'monospace',
                                                fontSize: '14px'
                                            }
                                        }}
                                    />
                                </Box>
                                
                                {/* Tag Validation Errors */}
                                {this.state.tagValidationErrors.length > 0 && (
                                    <Alert severity="error" sx={{ mb: 2 }}>
                                        <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                                            ⚠️ Fehlende Tags gefunden:
                                        </Typography>
                                        <Box component="ul" sx={{ margin: 0, paddingLeft: 2 }}>
                                            {this.state.tagValidationErrors.map((error, idx) => (
                                                <li key={idx}>
                                                    <Typography variant="body2" sx={{ fontSize: '13px' }}>
                                                        {error}
                                                    </Typography>
                                                </li>
                                            ))}
                                        </Box>
                                    </Alert>
                                )}
                                
                                {/* Placeholder Detection Indicator */}
                                {(() => {
                                    const detectedPlaceholders = [];
                                    if (this.state.emailBody.match(/\{\{\s*PRODUKTE\s*\}\}/)) detectedPlaceholders.push('PRODUKTE');
                                    if (this.state.emailBody.match(/\{\{\s*WARNING_START\s*\}\}/)) detectedPlaceholders.push('WARNING');
                                    if (this.state.emailBody.match(/\{\{\s*ERROR_START\s*\}\}/)) detectedPlaceholders.push('ERROR');
                                    if (this.state.emailBody.match(/\{\{\s*INFO_START\s*\}\}/)) detectedPlaceholders.push('INFO');
                                    if (this.state.emailBody.match(/\{\{\s*SUCCESS_START\s*\}\}/)) detectedPlaceholders.push('SUCCESS');
                                    if (this.state.emailBody.match(/\{\{\s*HIGHLIGHT_START\s*\}\}/)) detectedPlaceholders.push('HIGHLIGHT');
                                    
                                    if (detectedPlaceholders.length > 0) {
                                        return (
                                            <Box sx={{ mb: 3 }}>
                                                <Typography variant="caption" sx={{ color: '#666', mb: 1, display: 'block' }}>
                                                    Erkannte Platzhalter:
                                                </Typography>
                                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                                                    {detectedPlaceholders.map(placeholder => (
                                                        <Chip
                                                            key={placeholder}
                                                            label={`{{${placeholder}}}`}
                                                            size="small"
                                                            sx={{
                                                                bgcolor: '#e8f5e9',
                                                                color: '#2e7d32',
                                                                fontWeight: 600,
                                                                border: '1px solid #2e7d32',
                                                                fontFamily: 'monospace',
                                                                fontSize: '11px'
                                                            }}
                                                        />
                                                    ))}
                                                </Box>
                                            </Box>
                                        );
                                    }
                                    return null;
                                })()}

                                {/* Action Buttons */}
                                <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
                                    <Button
                                        variant="outlined"
                                        startIcon={<Preview />}
                                        onClick={this.handlePreview}
                                        sx={{
                                            borderColor: '#2e7d32',
                                            color: '#2e7d32',
                                            fontWeight: 600,
                                            '&:hover': {
                                                borderColor: '#1b5e20',
                                                bgcolor: '#e8f5e9'
                                            }
                                        }}
                                    >
                                        Vorschau
                                    </Button>
                                    <Button
                                        variant="contained"
                                        startIcon={<Send />}
                                        onClick={this.handleSend}
                                        disabled={this.state.sending || this.state.tagValidationErrors.length > 0}
                                        sx={{
                                            bgcolor: '#2e7d32',
                                            fontWeight: 600,
                                            '&:hover': {
                                                bgcolor: '#1b5e20'
                                            }
                                        }}
                                    >
                                        {this.state.sending ? 'Wird gesendet...' : 'E-Mail senden'}
                                    </Button>
                                </Box>
                            </Paper>
                        </Box>
                    </Box>
                </div>

                {/* Preview Dialog */}
                <Dialog
                    open={this.state.showPreview}
                    onClose={() => this.setState({ showPreview: false })}
                    maxWidth="lg"
                    fullWidth
                >
                    <DialogTitle>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                            <Typography variant="h6" sx={{ fontWeight: 700 }}>
                                E-Mail Vorschau
                            </Typography>
                            <IconButton onClick={() => this.setState({ showPreview: false })}>
                                <Close />
                            </IconButton>
                        </Box>
                    </DialogTitle>
                    <Divider />
                    <DialogContent>
                        <Alert severity="info" sx={{ mb: 2 }}>
                            <strong>Betreff:</strong> {this.state.emailSubject || '(Kein Betreff)'}
                        </Alert>
                        <Alert severity="success" sx={{ mb: 2 }}>
                            <strong>Empfänger:</strong> {this.state.selectedCustomers.length + this.state.customEmails.length} gesamt
                            {(this.state.selectedCustomers.length > 0 || this.state.customEmails.length > 0) && (
                                <span style={{ marginLeft: '8px', fontSize: '0.85em', opacity: 0.8 }}>
                                    ({this.state.selectedCustomers.length} Kunde(n) + {this.state.customEmails.length} benutzerdefinierte)
                                </span>
                            )}
                        </Alert>
                        <Paper elevation={2} sx={{ p: 2, bgcolor: '#f9f9f9', overflow: 'auto' }}>
                            <div dangerouslySetInnerHTML={{ __html: this.state.previewHTML }} />
                        </Paper>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={() => this.setState({ showPreview: false })}>
                            Schließen
                        </Button>
                    </DialogActions>
                </Dialog>
            </div>
        );
    }
}

export default DashboardEmailMarketing;
